import { motion } from "framer-motion";

// Editorial terminal card — inline SVG-style dashboard that reinforces "this is
// a real software product" without loading an image.
export default function HeroTerminal() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
            className="relative w-full max-w-md mx-auto lg:ml-auto lg:mr-0"
            data-testid="hero-terminal"
        >
            {/* Editorial caption — above card so card bottom aligns with CTAs */}
            <div className="mb-3 flex items-center gap-3 justify-end">
                <div className="w-8 h-px bg-gold"></div>
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/45">
                    Illustrative · anonymised borrower view
                </span>
            </div>

            {/* Card shell */}
            <div className="relative bg-navy text-paper border border-navy shadow-[0_30px_80px_-20px_rgba(11,31,42,0.35)]">
                {/* Chrome bar */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gold"></span>
                        <span className="w-2 h-2 rounded-full bg-white/25"></span>
                        <span className="w-2 h-2 rounded-full bg-white/15"></span>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50">
                        Carbonis Terminal · Live
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 lg:p-6 flex flex-col gap-4 lg:gap-5">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
                            Portfolio · Steel Exporter, Mid-cap
                        </div>
                        <div className="font-serif text-3xl lg:text-4xl leading-none text-paper">
                            44<span className="text-white/40">/100</span>
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-[11px]">
                            <span className="font-mono tracking-[0.2em] uppercase text-white/50">Moderate</span>
                            <span className="w-4 h-px bg-white/20"></span>
                            <span className="font-mono tracking-[0.2em] uppercase text-gold">Confidence 82%</span>
                        </div>
                    </div>

                    {/* Metric rows */}
                    <div className="flex flex-col gap-2.5 pt-3 border-t border-white/10">
                        <MetricRow label="CBAM Exposure" value="₹1.37 L" tone="warn" />
                        <MetricRow label="Capital Uplift" value="+72 bp" tone="warn" />
                        <MetricRow label="Scope 3 Multiple" value="8.4×" tone="neutral" />
                    </div>

                    {/* Scope bars */}
                    <div className="pt-3 border-t border-white/10">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">
                            Emissions · tCO₂e / yr
                        </div>
                        <ScopeBar label="Scope 1" pct={22} value="1,420" />
                        <ScopeBar label="Scope 2" pct={14} value="920" />
                        <ScopeBar label="Scope 3" pct={92} value="11,880" tone="gold" />
                    </div>

                    {/* Pulse footer */}
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                        <span className="relative flex w-2 h-2">
                            <span className="absolute inset-0 rounded-full bg-gold animate-ping opacity-75"></span>
                            <span className="relative w-2 h-2 rounded-full bg-gold"></span>
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
                            India CEA Grid EF v20 · updated 47s ago
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function MetricRow({ label, value, tone = "neutral" }) {
    const dot = tone === "warn" ? "bg-gold" : "bg-white/40";
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
                <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">{label}</span>
            </div>
            <span className="font-serif text-lg text-paper">{value}</span>
        </div>
    );
}

function ScopeBar({ label, pct, value, tone = "neutral" }) {
    const barColor = tone === "gold" ? "bg-gold" : "bg-white/60";
    return (
        <div className="mb-2 last:mb-0">
            <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/55">{label}</span>
                <span className="font-mono text-[10px] tracking-[0.12em] text-white/75">{value}</span>
            </div>
            <div className="h-[3px] w-full bg-white/10 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 1.4 }}
                    className={`h-full ${barColor}`}
                />
            </div>
        </div>
    );
}
